// Отмена заказа и подтверждение получения покупателем (fd-order-03).
//
// Что разрешает шлюз (`actorAllowed` в services/orders.ts):
//  - отмена — из `pending` (оформлен, не оплачен) и `new` (оплачен, продавец
//    ещё не подтвердил); дальше отменить может только продавец или поддержка;
//  - «получил» (`POST /buyer/orders/:id/complete`) — из `delivered`.
//
// Куда вернутся деньги — по тем же правилам, что у шлюза при отмене
// (`refundOnCancelTx`): часть, оплаченная балансом, сразу возвращается на
// баланс; карта CloudPayments — автоматический возврат в банк (срок зачисления
// зависит от банка, как в письме «Деньги возвращены»); остальные способы
// оплаты шлюз фиксирует как возврат вне системы — его проводит поддержка.
import type { Order } from "@/api";

const CANCELLABLE: ReadonlySet<Order["status"]> = new Set(["pending", "new"]);

export const canBuyerCancel = (order: Pick<Order, "status">): boolean => CANCELLABLE.has(order.status);

export const canBuyerConfirmReceipt = (order: Pick<Order, "status">): boolean => order.status === "delivered";

type RefundOrder = Pick<Order, "status" | "payment" | "paymentMode" | "balanceApplied" | "payableTotal" | "total"> & {
	refund?: Order["refund"];
};

/** Оплата прошла и деньги списаны провайдером (не наложенный платёж). */
const paidByProvider = (order: RefundOrder): boolean =>
	order.paymentMode !== "cod" && order.payment?.status === "approved";

/** Карта CloudPayments с номером транзакции — шлюз вернёт деньги автоматически. */
const cardRefundable = (order: RefundOrder): boolean =>
	paidByProvider(order) &&
	order.payment?.provider === "cloudpayments" &&
	/^\d+$/.test(order.payment?.transactionId ?? "");

/** Робокасса с API возвратов (fd-order-18): возврат на карту тоже автоматический. */
const apiRefundable = (order: RefundOrder, autoRefund: boolean): boolean =>
	autoRefund && paidByProvider(order) && order.payment?.provider === "robokassa";

/**
 * Строки «куда вернутся деньги» для окна отмены (до отмены) и для сообщения
 * после неё. После отмены шлюз возвращает `refund`: «refunded» подтверждает
 * возврат на карту, «pending» — возврат отправлен в платёжную систему и ждёт
 * её ответа, «external_settlement» — возврат вне системы. `autoRefund` —
 * платёжная система возвращает деньги через API (`/payments/config`).
 */
export const refundNotes = (
	order: RefundOrder,
	fmt: (value: number) => string,
	opts: { autoRefund?: boolean } = {},
): string[] => {
	const notes: string[] = [];
	const balance = order.balanceApplied ?? 0;
	if (balance > 0) notes.push(`${fmt(balance)} вернутся на баланс OSMIX сразу после отмены.`);
	const amount = Math.max(0, order.payableTotal ?? order.total - balance);
	if (order.paymentMode === "cod") {
		notes.push("Заказ с оплатой при получении - деньги не списывались.");
		return notes;
	}
	if (!paidByProvider(order) || amount <= 0) {
		if (balance <= 0) notes.push("Заказ не оплачен - деньги не списывались.");
		return notes;
	}
	if (order.refund?.status === "pending") {
		notes.push(`${fmt(amount)} вернутся на карту: возврат отправлен в платёжную систему. Срок зачисления зависит от банка.`);
		return notes;
	}
	const refunded =
		order.refund?.status === "refunded" ||
		(!order.refund && (cardRefundable(order) || apiRefundable(order, Boolean(opts.autoRefund))));
	if (refunded) {
		const card = order.payment?.last4 ? ` •••• ${order.payment.last4}` : "";
		notes.push(`${fmt(amount)} вернутся на карту${card}. Срок зачисления зависит от банка.`);
	} else {
		notes.push(`${fmt(amount)} вернёт поддержка OSMIX тем же способом оплаты: автоматический возврат для него недоступен.`);
	}
	return notes;
};
